import React, { useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useGetPositionsForIncidentId,
  useRosterTemplatesCatalog,
} from 'hooks/incident/useRoster';

import type { FileScopeType } from './hooks/useGetAllFiles';

export type RosterPositionTag = {
  positionName: string | null;
  positionTemplateSlug: string | null;
};

const NONE = '__none__';
const TAG_SEP = '::';

const encodeOrgTag = (slug: string, name: string) => `${slug}${TAG_SEP}${name}`;

const decodeOrgTag = (
  value: string
): { positionName: string | null; positionTemplateSlug: string | null } => {
  const sep = value.indexOf(TAG_SEP);
  if (sep <= 0) {
    return { positionName: null, positionTemplateSlug: null };
  }
  return {
    positionTemplateSlug: value.slice(0, sep),
    positionName: value.slice(sep + TAG_SEP.length),
  };
};

/**
 * Tags a folder with a roster position. Incident folders choose from that
 * incident's own seats. Org folders pick a seat from a roster template in one
 * grouped select — an org has no single org chart to point at.
 *
 * The tag is a seat _name_, so renaming the seat on the org chart detaches the
 * folder. That is the cost of positions being re-created per incident: there is
 * no id that means the same seat in two incidents.
 */
export const RosterPositionTagField: React.FC<{
  ownerId: string;
  scopeType?: FileScopeType;
  value: RosterPositionTag;
  onChange: (next: RosterPositionTag) => void;
}> = ({ ownerId, scopeType, value, onChange }) => {
  const isOrgScope = scopeType === 'organization';

  const { data: incidentPositions = [] } = useGetPositionsForIncidentId(
    isOrgScope ? '' : ownerId
  );
  const { data: catalog } = useRosterTemplatesCatalog();

  const templates = useMemo(
    () => (catalog?.templates ?? []).filter((entry) => !entry.attachOnly),
    [catalog]
  );

  const items = useMemo(() => {
    const none = { value: NONE, label: 'Not tagged' };
    if (isOrgScope) {
      return [
        none,
        ...templates.flatMap((template) =>
          (template.nodes ?? []).map((seat) => ({
            value: encodeOrgTag(template.slug, seat.name),
            label: seat.name,
          }))
        ),
      ];
    }
    return [
      none,
      ...incidentPositions.map((position) => ({
        value: position.name,
        label: position.name,
      })),
    ];
  }, [incidentPositions, isOrgScope, templates]);

  const selectedValue = isOrgScope
    ? value.positionName && value.positionTemplateSlug
      ? encodeOrgTag(value.positionTemplateSlug, value.positionName)
      : NONE
    : (value.positionName ?? NONE);

  return (
    <div className="dark mb-3">
      <label className="form-label">Roster position (optional)</label>
      <p className="text-muted small mb-2">
        Files in this folder are listed on tasks assigned to this position, or
        to anyone sitting in it. Renaming the seat later detaches the folder.
      </p>

      <Select
        value={selectedValue}
        items={items}
        onValueChange={(next) => {
          if (next == null || next === NONE) {
            onChange({ positionName: null, positionTemplateSlug: null });
            return;
          }
          if (isOrgScope) {
            onChange(decodeOrgTag(String(next)));
            return;
          }
          onChange({
            positionName: String(next),
            positionTemplateSlug: value.positionTemplateSlug,
          });
        }}
      >
        <SelectTrigger className="tw:w-full" aria-label="Roster position">
          <SelectValue placeholder="Not tagged" />
        </SelectTrigger>
        <SelectContent
          className="dark"
          positionerClassName="tw:z-[1100]"
          alignItemWithTrigger={false}
        >
          <SelectItem value={NONE}>Not tagged</SelectItem>
          {isOrgScope
            ? templates.map((template) => (
                <SelectGroup key={template.slug}>
                  <SelectLabel>{template.name}</SelectLabel>
                  {(template.nodes ?? []).map((seat) => (
                    <SelectItem
                      key={encodeOrgTag(template.slug, seat.name)}
                      value={encodeOrgTag(template.slug, seat.name)}
                    >
                      {seat.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))
            : incidentPositions.map((position) => (
                <SelectItem key={position.name} value={position.name}>
                  {position.name}
                </SelectItem>
              ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default RosterPositionTagField;
